import { useState, useEffect, useCallback } from 'react';
import {
  workflowService,
  type WorkflowSummaryResponse,
  type WorkflowValidationRequest,
} from '../services/workflow';
import toast from 'react-hot-toast';

export function useWorkflowValidation(laporanId: number | null) {
  const [summary, setSummary] = useState<WorkflowSummaryResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [processing, setProcessing] = useState(false);

  const fetchSummary = useCallback(async () => {
    if (!laporanId) return;
    setLoading(true);
    try {
      const data = await workflowService.getSummary(laporanId);
      setSummary(data);
    } catch (err) {
      console.error('[WorkflowValidation] Failed to load summary:', err);
      setSummary(null);
    } finally {
      setLoading(false);
    }
  }, [laporanId]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // ✅ FIX: currentStep hanya dari backend
  // Filter step yang benar-benar pending DAN sesuai current_validation_step dari backend
  const currentStep = summary?.current_step != null
    ? summary?.steps?.find(
        (s) => s.step_order === summary.current_step && s.status === 'pending'
      ) || null
    : null;

  // ✅ Tambahkan: apakah ada step pending aktif (untuk validasi tombol aksi)
  const hasActivePendingStep = summary?.steps?.some((s) => s.status === 'pending') ?? false;

  const canApprove = summary?.can_approve ?? false;
  const canReject = summary?.can_reject ?? false;
  const canResolve = summary?.can_resolve ?? false;

  const processAction = useCallback(
    async (
      action: 'approve' | 'reject' | 'resolve',
      payload?: string | WorkflowValidationRequest
    ): Promise<boolean> => {
      if (!laporanId) return false;
      setProcessing(true);
      try {
        await workflowService.processValidation(laporanId, action, payload);
        const messages = {
          approve: 'Laporan berhasil disetujui.',
          reject: 'Laporan ditolak dan dikembalikan ke pelapor.',
          resolve: 'Laporan diselesaikan di level Anda.',
        };
        toast.success(messages[action]);
        await fetchSummary();
        return true;
      } catch (err: any) {
        const msg = err?.response?.data?.message || 'Gagal memproses validasi.';
        toast.error(msg);
        return false;
      } finally {
        setProcessing(false);
      }
    },
    [laporanId, fetchSummary]
  );

  return {
    summary,
    loading,
    processing,
    currentStep,
    canApprove,
    canReject,
    canResolve,
    hasActivePendingStep,
    approve: (payload?: string | WorkflowValidationRequest) => processAction('approve', payload),
    reject: (feedback: string) => processAction('reject', feedback),
    resolve: (payload: WorkflowValidationRequest) => processAction('resolve', payload),
    refresh: fetchSummary,
  };
}
